import {boardCellKey} from '@github-ui/board-core'

import {
  type BoardInitialPayload,
  type BoardPayloadItem,
  NO_VALUE_KEY,
  toBoardData as toBoardDataShape,
} from './issues/board-payload'
import {normalizeBoardMoves} from './board-move-axes'
import type {BoardCellCursors, SavedViewsBoardData} from './types'

/**
 * What the no-value bucket is called on an axis whose field the payload did not name: a
 * response from a server that predates `columnFieldLabel`.
 */
const UNNAMED_NO_VALUE_LABEL = 'No value'

/**
 * What the bucket holding the items with no value for an axis is called, in the axis field's
 * own words: `No Priority` for a board arranged by `Priority`.
 *
 * The name comes from the payload rather than from the field list, which Planning fetches in
 * the browser after the board has already been server-rendered
 * (`PlanningViewOptionsMenu`). Reading it there would leave the server-rendered heading blank
 * and rename it on hydration.
 */
function noValueLabel(fieldLabel: string | null | undefined): string {
  return fieldLabel == null ? UNNAMED_NO_VALUE_LABEL : `No ${fieldLabel}`
}

/**
 * Names the reserved no-value entry on one axis, leaving every other entry alone.
 *
 * Named here rather than in a column header, because `label` is also what `useBoardCell`
 * and `useBoardRow` build their accessible names from: a heading fixed on its own would
 * leave every cell in the column announced under an empty name.
 */
function nameNoValueBucket<TAxis extends {key: string; label: string}>(
  axis: readonly TAxis[],
  fieldLabel: string | null | undefined,
): TAxis[] {
  return axis.map(entry =>
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- `key` here is the axis entry's wire-contract key, not a keyboard event's
    entry.key === NO_VALUE_KEY ? {...entry, label: noValueLabel(fieldLabel)} : entry,
  )
}

/**
 * Keyed by board-core's own cell key: bucket keys are opaque and may hold any character, so a
 * bare join collides and a collision would hand one cell another's cursor.
 */
function cellCursors(payload: BoardInitialPayload): BoardCellCursors {
  const cursors = new Map<string, string>()
  for (const cell of payload.cells) {
    if (cell.itemsCursor != null) cursors.set(boardCellKey(cell), cell.itemsCursor)
  }
  return cursors
}

/**
 * Maps a payload into everything the host needs: the board `data`, the axis field identifiers
 * that drive grouping, and where each cell left off. Pure — no React, no fetch, no domain
 * rules.
 *
 * The `data` mapping is delegated to the payload module's `toBoardData`, so the D16
 * discipline — four stated axis booleans, per-cell `totalCount`/`hasMoreItems` carried
 * rather than inferred from `items.length` — lives in exactly one place. The no-value
 * bucket is named on top of it, because the payload deliberately leaves that name blank.
 */
export function toBoardData(payload: BoardInitialPayload): SavedViewsBoardData<BoardPayloadItem> {
  const data = toBoardDataShape(payload)
  return normalizeBoardMoves({
    data: {
      ...data,
      columns: nameNoValueBucket(data.columns, payload.columnFieldLabel),
      rows: nameNoValueBucket(data.rows, payload.rowFieldLabel),
    },
    columnField: payload.columnField,
    rowField: payload.rowField,
    viewerCanMoveItems: payload.viewerCanMoveItems,
    viewerCanMoveBetweenColumns: payload.viewerCanMoveBetweenColumns,
    viewerCanMoveBetweenRows: payload.viewerCanMoveBetweenRows,
    cellCursors: cellCursors(payload),
  })
}
